export type CleaningCondition = 'light' | 'average' | 'heavy';

export interface CleaningQuoteInput {
  bedrooms: number;
  bathrooms: number;
}

export interface CleaningQuoteResult {
  estimatedPrice: number;
  lowPrice: number;
  highPrice: number;
  estimatedHours: number;
  priceLabel: string;
  rangeLabel: string;
  hoursLabel: string;
  disclaimer: string;
}

export class CleaningQuoteCalculator {
  // Base model:
  // 2 bed / 1 bath = $129
  // Then add per-bedroom and per-bath increments
  private static readonly BASE_PRICE = 129;
  private static readonly BASE_BEDROOMS = 2;
  private static readonly BASE_BATHROOMS = 1;

  private static readonly EXTRA_BEDROOM_PRICE = 18;
  private static readonly EXTRA_BATHROOM_PRICE = 22;

  // Optional visit-condition adjustments used only for the final on-site confirmation
  private static readonly CONDITION_MULTIPLIERS: Record<CleaningCondition, number> = {
    light: 0.95,
    average: 1.0,
    heavy: 1.15,
  };

  // Simple hours model
  private static readonly BASE_HOURS = 2.5;
  private static readonly EXTRA_BEDROOM_HOURS = 0.35;
  private static readonly EXTRA_BATHROOM_HOURS = 0.4;

  public static calculate(input: CleaningQuoteInput): CleaningQuoteResult {
    const bedrooms = this.normalizeBedrooms(input.bedrooms);
    const bathrooms = this.normalizeBathrooms(input.bathrooms);

    const extraBedrooms = Math.max(0, bedrooms - this.BASE_BEDROOMS);
    const extraBathrooms = Math.max(0, bathrooms - this.BASE_BATHROOMS);

    const estimatedPrice =
      this.BASE_PRICE +
      extraBedrooms * this.EXTRA_BEDROOM_PRICE +
      extraBathrooms * this.EXTRA_BATHROOM_PRICE;

    const lightPrice = this.applyConditionMultiplier(estimatedPrice, 'light');
    const heavyPrice = this.applyConditionMultiplier(estimatedPrice, 'heavy');

    const estimatedHours =
      this.BASE_HOURS +
      extraBedrooms * this.EXTRA_BEDROOM_HOURS +
      extraBathrooms * this.EXTRA_BATHROOM_HOURS;

    return {
      estimatedPrice,
      lowPrice: lightPrice,
      highPrice: heavyPrice,
      estimatedHours: this.roundHours(estimatedHours),
      priceLabel: `$${estimatedPrice}`,
      rangeLabel: `$${lightPrice}-$${heavyPrice}`,
      hoursLabel: `${this.roundHours(estimatedHours)} hrs`,
      disclaimer:
        'Instant estimate shown online. Final price confirmed upon arrival based on home condition: light, average, or heavy.',
    };
  }

  public static getFinalConfirmedPrice(
    input: CleaningQuoteInput,
    condition: CleaningCondition
  ): number {
    const quote = this.calculate(input);
    return this.applyConditionMultiplier(quote.estimatedPrice, condition);
  }

  private static applyConditionMultiplier(
    amount: number,
    condition: CleaningCondition
  ): number {
    return Math.round(amount * this.CONDITION_MULTIPLIERS[condition]);
  }

  private static normalizeBedrooms(value: number): number {
    if (!Number.isFinite(value)) return this.BASE_BEDROOMS;
    return Math.max(1, Math.floor(value));
  }

  private static normalizeBathrooms(value: number): number {
    if (!Number.isFinite(value)) return this.BASE_BATHROOMS;
    return Math.max(1, Math.round(value * 2) / 2); // allows .5 baths
  }

  private static roundHours(value: number): number {
    return Math.round(value * 10) / 10;
  }
}
